import type {PaginationWrapper} from '@/composables/utils/paginationWrapper';
import type {PaginatedResponse} from '@myparcel/sdk';
import type {PostUser, PutUser} from '@myparcel-frontend/sdk';
import type {ValueOf, User} from '@myparcel-frontend/types';
import type {Ref} from 'vue';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {pollUntil, retry} from '@myparcel-frontend/utils';
import {storeToRefs} from 'pinia';
import {ref} from 'vue';
import paginationWrapper from '@/composables/utils/paginationWrapper';
import {useUsersStore} from '@/store/usersStore/usersStore';
import logToMonitor from '@/utils/logging';

export interface UserFilter {
  page?: number;
  size?: number;
}

export default function useUser() {
  const usersStore = useUsersStore();
  const {userList} = storeToRefs(usersStore);

  const getAllUsers = async (): Promise<{
    users: User[];
    succeeded?: boolean;
  }> => {
    if (userList.value.length) {
      return {users: userList.value, succeeded: undefined};
    }

    // If the initial response fails we throw the error to the parent and handle it there.
    const initialResponse = await getUsers(ref(Status.Idle))({page: 1});

    const amountOfCallsNeeded = Math.ceil(initialResponse.results / initialResponse.data.length);
    const getUsersPromises: Promise<PaginationWrapper<User>>[] = [];
    const startingPageAfterInitialCall = 2;

    // For-loop is used to create the promise array. It will not be looped over if all users are returned in the initial call
    for (let page = startingPageAfterInitialCall; page <= amountOfCallsNeeded; page++) {
      const getUsersCallback = (pageParam: number) => getUsers(ref(Status.Idle))({page: pageParam});
      const apiCallWithRetry = retry<PaginationWrapper<User>>(() => getUsersCallback(page), 1);

      getUsersPromises.push(apiCallWithRetry);
    }

    const results = await Promise.allSettled(getUsersPromises);

    const rejectedApiCalls = results.filter((result) => result.status === 'rejected');
    const succeededApiCalls = results.filter((result) => result.status === 'fulfilled');

    /* istanbul ignore next */
    rejectedApiCalls.forEach((result) => {
      logToMonitor(Error(result.reason as string), {
        _myparcel_extra_info: 'Fetching paginated users failed in useUser composable',
      });
    });

    const succeededApiCallResults = succeededApiCalls.map((result) => result.value);
    const users = [initialResponse, ...succeededApiCallResults].flatMap((userPage) => userPage.data);

    usersStore.updateUserList(users);

    return {users: userList.value, succeeded: rejectedApiCalls.length === 0};
  };

  const getUsers =
    (status: Ref<ValueOf<typeof Status>>) =>
    async ({page, size}: UserFilter): Promise<PaginationWrapper<User>> => {
      const {sdk} = useSdk();
      let result;
      status.value = Status.PENDING;

      const accessor = (response: PaginatedResponse<User[]>): User[] => response.users;

      try {
        result = await paginationWrapper<User>(
          sdk.getUsers,
          {
            ...(page && {page}),
            ...(size && {size}),
          },
          accessor,
        );
        status.value = Status.RESOLVED;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to get users in useUser composable',
        });
        status.value = Status.REJECTED;
        throw error;
      }

      return result;
    };

  const getUser =
    (status: Ref<ValueOf<typeof Status>>) =>
    async (id: number): Promise<User> => {
      const {sdk} = useSdk();
      status.value = Status.PENDING;
      let result;

      try {
        result = await sdk.getUser({
          path: {id},
        });

        result = result[0];
        status.value = Status.RESOLVED;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to get user in useUser composable',
        });
        status.value = Status.REJECTED;
        throw error;
      }

      return result;
    };

  const deleteUser =
    (status: Ref<ValueOf<typeof Status>>) =>
    async (id: number): Promise<void> => {
      const {sdk} = useSdk();
      status.value = Status.PENDING;

      try {
        await sdk.deleteUser({
          path: {id},
        });
        status.value = Status.RESOLVED;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to delete user in useUser composable',
        });
        status.value = Status.REJECTED;
        throw error;
      }
    };

  const createUser =
    (status: Ref<ValueOf<typeof Status>>) =>
    async (user: PostUser): Promise<number> => {
      const {sdk} = useSdk();
      status.value = Status.PENDING;
      let result;

      try {
        result = (
          await sdk.postUsers({
            body: [user],
          })
        ).ids[0];
        status.value = Status.RESOLVED;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to create user in useUser composable',
        });
        status.value = Status.REJECTED;
        throw error;
      }

      return result;
    };

  const editUser =
    (status: Ref<ValueOf<typeof Status>>) =>
    async (user: PutUser): Promise<void> => {
      const {sdk} = useSdk();
      status.value = Status.PENDING;

      try {
        await sdk.putUsers({
          body: [user],
        });
        status.value = Status.RESOLVED;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to edit users in useUser composable',
        });
        status.value = Status.REJECTED;
        throw error;
      }
    };

  const waitForUserCreated = async (userId: number): Promise<boolean> => {
    const {sdk} = useSdk();

    const result = await pollUntil(
      // Poll the list endpoint with the user id to verify ES has indexed the new user.
      () =>
        sdk
          .getUsers({
            parameters: {
              id: userId,
              page: 1,
              size: 1,
            },
          })
          .then((response) => response.users.length > 0),
      (isCreated) => isCreated,
      {
        interval: 1000,
        timeout: 10_000,
      },
    );

    return Boolean(result);
  };

  const waitForUserDeleted = async (userId: number): Promise<boolean> => {
    const {sdk} = useSdk();

    const result = await pollUntil(
      // Poll the list endpoint with the user id to verify ES has de-indexed the deleted user.
      () =>
        sdk
          .getUsers({
            parameters: {
              id: userId,
              page: 1,
              size: 1,
            },
          })
          .then((response) => response.users.length === 0),
      (isDeleted) => isDeleted,
      {
        interval: 1000,
        timeout: 10_000,
      },
    );

    return Boolean(result);
  };

  return {
    users: userList,
    getUsersByShopIds: usersStore.getUsersByShopIds,
    getUsernameByUserId: usersStore.getUsernameByUserId,
    deleteUser,
    getAllUsers,
    getUsers,
    getUser,
    createUser,
    editUser,
    waitForUserCreated,
    waitForUserDeleted,
  };
}
